import React, { forwardRef, useState } from 'react'
import { Grid } from '@mui/material'
import { makeStyles } from '@mui/styles'

import { Eye as EyeIcon, EyeOff as EyeOffIcon } from '../../icons'
import * as validators from '../../utils/validation'
import PasswordAdornment from './PasswordAdornment'
import PuiTextField from './PuiTextField'

const useStyles = makeStyles(
  () => ({
    eyeIcon: {
      opacity: 0.5,
      width: 29,
      height: 29,
    },
    eyeContainer: {
      cursor: 'pointer',
      display: 'flex',
    },
  }),
  { name: 'PasswordInput' },
)

interface PasswordInputProps {
  readonly adornmentEnabled?: boolean
  readonly autoFocus?: boolean
  readonly className?: string
  readonly disabled?: boolean
  readonly error?: boolean
  readonly id?: string
  readonly label?: string
  readonly onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void
  readonly onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void
  readonly onMouseDown?: (event: React.MouseEvent<HTMLInputElement>) => void
  readonly onShowPassword?: (show: boolean) => void
  readonly password: string
  readonly validatorAdornment?: React.ReactNode
}

const PasswordInput = forwardRef<HTMLDivElement, PasswordInputProps>(
  function PasswordInput(
    {
      error,
      disabled,
      autoFocus,
      password,
      onChange,
      onBlur,
      onMouseDown,
      id = 'password',
      label: labelProp,
      onShowPassword,
      adornmentEnabled,
      validatorAdornment,
      className,
    },
    ref,
  ) {
    const classes = useStyles()
    const [showPassword, setShowPassword] = useState(false)

    const label = labelProp || 'Password'

    const onEyeClick = () => {
      if (onShowPassword) {
        onShowPassword(!showPassword)
      }
      setShowPassword(!showPassword)
    }

    return (
      <PuiTextField
        InputProps={{
          endAdornment: (
            <>
              {adornmentEnabled && (
                <PasswordAdornment
                  condition={validators.isPasswordValid(password)}
                  currentLength={password.length}
                />
              )}
              {validatorAdornment}
              <Grid item className={classes.eyeContainer} onClick={onEyeClick}>
                {showPassword ? (
                  <EyeOffIcon
                    className={classes.eyeIcon}
                    preserveAspectRatio="none"
                  />
                ) : (
                  <EyeIcon
                    className={classes.eyeIcon}
                    preserveAspectRatio="none"
                  />
                )}
              </Grid>
            </>
          ),
        }}
        autoComplete="current-password"
        autoFocus={autoFocus}
        className={className}
        disabled={disabled}
        error={error}
        id={id}
        label={label}
        ref={ref}
        type={showPassword ? 'text' : 'password'}
        value={password}
        onBlur={onBlur}
        onChange={onChange}
        onMouseDown={onMouseDown}
      />
    )
  },
)

export default PasswordInput
